import { axiosInstance } from "../../../api/axiosInstance.tsx";

export interface employeeSearchParams {
  name?: string;
  nik?: string;
  position?: string;
  jobTitle?: string;
  division?: string;
  gender?: string;
  status?: string;
  employeeType?: string;
  maritalStatus?: string;
  office?: string;
  company?: string;
  dateOfJoining?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

// Define the interface at the top of the file
interface RegisterUserParams {
  email: string;
  username: string;
  password: string;
}

interface UserRegistrationResponse {
  id: string;
  username: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

interface RoleAssignmentParams {
  roleName: string;
}

interface Employee {
  id: string;
  name: string;
  nik: string;
  position: string;
  division: string;
  office: string;
  status: string;
  employeeType: string;
  userId: string; // Tambahkan ini
  email: string; // Tambahkan ini jika belum ada
}

interface UserInfoResponse {
  id: string;
  email: string;
  roles: string[];
  // tambahkan field lain sesuai response API
}

export const fetchEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await axiosInstance.get("/employees/view_employees");
    return response.data;
  } catch (error) {
    throw new Error("Gagal mengambil data karyawan");
  }
};

// Helper function untuk format tampilan position
export const formatPositionDisplay = (position: string | string[]) => {
  try {
    // Jika position adalah string JSON, parse dulu
    const parsedPosition =
      typeof position === "string" ? JSON.parse(position) : position;

    // Jika hasilnya array, gabungkan dengan koma
    return Array.isArray(parsedPosition) ? parsedPosition.join(", ") : position;
  } catch {
    // Jika parsing gagal, kembalikan as-is
    return position;
  }
};

export const getEmployees = async (params: employeeSearchParams = {}) => {
  const response = await axiosInstance.get("/employees/search_employees", {
    params,
  });

  // Format position data di response
  const formattedData = response.data.data.map((employee) => ({
    ...employee,
    position: formatPositionDisplay(employee.position),
  }));

  return {
    ...response.data,
    data: formattedData,
  };
};

// Step 1: Create Employee
export const createEmployee = async (data) => {
  const payload = {
    email: data.email,
    name: data.name,
    nik: data.nik,
    position: Array.isArray(data.position)
      ? JSON.stringify(data.position)
      : data.position,
    directorate: data.directorate,
    division: data.division,
    divisionDropdown: data.division,
    jobTitle: data.jobTitle,
    address: data.address,
    gender: data.gender,
    dateOfBirth: data.dateOfBirth,
    cityOfBirth: data.cityOfBirth,
    religion: data.religion,
    taxIdNumber: data.taxIdNumber,
    bankName: data.bankName,
    bankNumber: data.bankNumber,
    bankAccountName: data.bankAccountName,
    phone: data.phone,
    status: data.status,
    dateOfJoining: data.dateOfJoining,
    salary: data.salary,
    salaryCoefficient: data.salaryCoefficient,
    salaryDelta: data.salaryDelta,
    contributionAllowance: data.contributionAllowance,
    serviceAllowance: data.serviceAllowance,
    pembinaan: data.coachingAllowance,
    transportAllowance: data.transportAllowance,
    housingAllowance: data.housingAllowance,
    telkomAllowance: data.telecommunicationAllowance,
    positionAllowance: data.positionAllowance,
    pulsaAllowance: data.mobilePhoneAllowance,
    overtimeAllowance: data.overtimeAllowance,
    naturaAllowance: data.naturaAllowance,
    otherAllowance: data.otherAllowance,
    notes: data.notes,
    //totalAnak: data.totalAnak,
    office: data.officeId || data.office,
    effectiveDate: data.effectiveDate,
    employeeType: data.employeeType,
    company: data.companyId || data.company,
    expiredDate: data.expiredDate,
    //maritalStatus: data.maritalStatus,
    potBpjsKes: data.bpjsKesehatan,
    potBpjsTk: data.bpjsTK,
    ptkpCategory: data.pTKPCategory,
    kategoriTER: data.categoryTER,
    //potPph21: data.pph21,
    grossSalary: data.grossSalary,
    netSalary: data.nettSalary
  };
  const response = await axiosInstance.post(
    `employees/create_employee`,
    payload
  );
  return response.data; // contains the item `id` or `code`
};

// Step 2: Upload Photo by Item ID
export const uploadItemPhoto = async (id, file) => {
  const formData = new FormData();
  formData.append("photo", file);

  return axiosInstance.post(`/employees/upload_photo/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const fetchEmployeePhotoBlob = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/employees/photo/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log(response.data);
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};

// Delete employee
export const deleteEmployee = async (id: string) => {
  try {
    await axiosInstance.delete(`/employees/delete_employee/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat menghapus karyawan"
    );
  }
};

export const deleteEmployeeByEmail = async (email: string) => {
  try {
    await axiosInstance.delete("/admin/delete-by-email", {
      data: { email },
    });
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat menghapus karyawan"
    );
  }
};

// Update Employee
export const updateEmployee = async (id, data) => {
  const payloadEdit = {
    email: data.email,
    name: data.name,
    nik: data.nik,
    position: Array.isArray(data.position)
      ? JSON.stringify(data.position)
      : data.position,
    directorate: data.directorate,
    division: data.division,
    divisionDropdown: data.division,
    jobTitle: data.jobTitle,
    address: data.address,
    gender: data.gender,
    dateOfBirth: data.dateOfBirth,
    cityOfBirth: data.cityOfBirth,
    religion: data.religion,
    taxIdNumber: data.taxIdNumber,
    bankName: data.bankName,
    bankNumber: data.bankNumber,
    bankAccountName: data.bankAccountName,
    phone: data.phone,
    status: data.status,
    dateOfJoining: data.dateOfJoining,
    salary: data.salary,
    salaryCoefficient: data.salaryCoefficient,
    salaryDelta: data.salaryDelta,
    contributionAllowance: data.contributionAllowance,
    serviceAllowance: data.serviceAllowance,
    pembinaan: data.coachingAllowance,
    transportAllowance: data.transportAllowance,
    housingAllowance: data.housingAllowance,
    telkomAllowance: data.telecommunicationAllowance,
    positionAllowance: data.positionAllowance,
    pulsaAllowance: data.mobilePhoneAllowance,
    overtimeAllowance: data.overtimeAllowance,
    naturaAllowance: data.naturaAllowance,
    otherAllowance: data.otherAllowance,
    notes: data.notes,
    //totalAnak: data.totalAnak,
    office: data.officeId || data.office,
    effectiveDate: data.effectiveDate,
    employeeType: data.employeeType,
    company: data.companyId || data.company,
    expiredDate: data.expiredDate,
    //maritalStatus: data.maritalStatus,
    potBpjsKes: data.bpjsKesehatan,
    potBpjsTk: data.bpjsTK,
    ptkpCategory: data.pTKPCategory,
    kategoriTER: data.categoryTER,
    //potPph21: data.pph21,
    grossSalary: data.grossSalary,
    netSalary: data.nettSalary
  };
  const response = await axiosInstance.put(
    `/employees/update_employee/${id}`,
    payloadEdit
  );
  return response.data;
};

// Fetch Employee Details by ID
export const getEmployeeById = async (id) => {
  try {
    const response = await axiosInstance.get(`/employees/view_employee/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

// Fungsi untuk mencari karyawan berdasarkan keyword
export const sesarchEmployees = async (searchQuery: string) => {
  try {
    const response = await axiosInstance.get(
      `/employees/search_employees?keyword=${searchQuery}`
    );
    return response.data; // Pastikan response sesuai format yang diterima tabel
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

export const fetchRoles = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/rbac/roles");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Gagal mengambil data roles"
    );
  }
};

export const fetchVendors = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/vendors/view_vendors");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data vendor. Silakan coba lagi."
    );
  }
};

export const fetchOffices = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/offices/view_offices");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data office. Silakan coba lagi."
    );
  }
};

// Update the registerUser function to use the interface
export const registerUser = async (
  data: RegisterUserParams
): Promise<UserRegistrationResponse> => {
  try {
    const username = data.email.split("@")[0];
    const response = await axiosInstance.post("/auth/register", {
      ...data,
      username,
      forcePasswordChange: true,
    });
    return response.data.user; // Return the user object directly
  } catch (error) {
    throw error.response?.data?.message || "Failed to register user";
  }
};

// Assign role to user
export const assignRoleToUser = async (
  userId: string,
  data: RoleAssignmentParams
) => {
  try {
    const response = await axiosInstance.post("/auth/assign-role", {
      userId,
      roleName: data.roleName,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to assign role";
  }
};

// Add this to employeeService.tsx
export const assignRolesToUser = async (
  userId: string,
  email: string,
  roleNames: string[]
) => {
  try {
    const response = await axiosInstance.post("/auth/assign-roles", {
      userId,
      email,
      roleNames,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to assign roles";
  }
};

// Fungsi untuk unassign role dari user
export const unassignRoleFromUser = async (
  email: string,
  userId: string,
  roleNames: string[]
) => {
  try {
    const response = await axiosInstance.post("/auth/unassign-role", {
      email,
      userId,
      roleNames,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to unassign role";
  }
};

// Combined function to create employee and user account
// Remove the generateRandomPassword function and modify createEmployeeWithAccount
export const createEmployeeWithAccount = async (
  employeeData: any,
  file?: File | null
): Promise<{
  employee: any;
  user: UserRegistrationResponse;
  message: string;
}> => {
  try {
    // 1. First check if email already exists
    let userResponse: UserRegistrationResponse | null = null;
    let isExistingUser = false;

    try {
      userResponse = await getUserInfoByEmail(employeeData.email);
      isExistingUser = true;
    } catch (error) {
      // Email doesn't exist, we'll register new user later
    }

    // 2. Create employee record
    const employeeResponse = await createEmployee(employeeData);

    // 3. Upload photo if available
    if (file) {
      await uploadItemPhoto(employeeResponse.id, file);
    }

    // 4. Register user only if email doesn't exist
    if (!isExistingUser) {
      userResponse = await registerUser({
        email: employeeData.email,
        username: employeeData.username || employeeData.email.split("@")[0],
        password: "qwerty123!",
      });
    }

    // At this point, userResponse should never be null, but we'll check to be safe
    if (!userResponse) {
      throw new Error("Failed to get or create user account");
    }

    // 5. Assign all roles to the user
    if (employeeData.position) {
      const rolesToAssign = Array.isArray(employeeData.position)
        ? employeeData.position
        : [employeeData.position];

      if (rolesToAssign.length > 0) {
        await assignRolesToUser(
          userResponse.id,
          employeeData.email,
          rolesToAssign
        );
      }
    }

    return {
      employee: employeeResponse,
      user: userResponse,
      message: isExistingUser
        ? "Employee created and roles assigned to existing user"
        : "Account created and roles assigned successfully",
    };
  } catch (error) {
    throw error;
  }
};

export const getUserInfoByEmail = async (email: string) => {
  try {
    const response = await axiosInstance.get("/admin/user-info", {
      params: { email },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to get user info";
  }
};

// Helper function to generate random password
const generateRandomPassword = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};
